// Disponibilité d'une ligne de devis : stock disponible, puis commandes fournisseurs en cours et
// délai d'approvisionnement s'il ne couvre pas la quantité. Même règle que
// App\Services\Trade\LineAvailability, côté serveur.
// @see docs/DRI/Phase3/disponibilite-devis.md § 6 et disponibilite-partielle.md § 5

const EPSILON = 0.0001;

/** Une ligne qui porte un article et une quantité ; au forfait, le nombre d'unités n'est pas connu. */
function isCountable(line) {
    return !!line.article_id && line.is_title != 1 && !line.is_eco_participation
        && String(line.unit || '').trim().toLowerCase() !== 'forfait';
}

/** Une quantité sans décimales inutiles : « 12 » et non « 12,000 ». */
function format(value) {
    return (parseFloat(value) || 0).toFixed(3).replace(/\.?0+$/, '').replace('.', ',');
}

function stockColor(stock, covers) {
    if (covers) {
        return 'green';
    }

    return stock > 0 ? 'orange' : 'red';
}

/** Le besoin d'un article sur tout le devis ; à défaut, la quantité de la ligne seule. */
function needOf(demand, articleId, fallback) {
    return demand && demand[articleId] ? demand[articleId].quantity : fallback;
}

/** « Demandé sur ce devis : 7 U (2 lignes, 1 kit) », quand d'autres lignes puisent dans le même stock. */
function demandLabel(entry, unit) {
    const parts = [];
    if (entry.lines > 0) {
        parts.push(entry.lines + (entry.lines > 1 ? ' lignes' : ' ligne'));
    }
    if (entry.kits > 0) {
        parts.push(entry.kits + (entry.kits > 1 ? ' kits' : ' kit'));
    }

    return 'Demandé sur ce devis : ' + format(entry.quantity) + ' ' + (unit || 'U') + ' (' + parts.join(', ') + ')';
}

function isShared(entry) {
    return !!entry && entry.lines + entry.kits > 1;
}

/** Aujourd'hui, au format AAAA-MM-JJ. */
export function todayString() {
    const now = new Date();
    const pad = value => String(value).padStart(2, '0');

    return now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
}

/** Le nombre de jours entre deux dates AAAA-MM-JJ, négatif si la seconde précède la première. */
function daysBetween(from, to) {
    const parse = value => Date.UTC(...value.split('-').map((part, index) => index === 1 ? part - 1 : +part));

    return Math.round((parse(to) - parse(from)) / 86400000);
}

/**
 * Ce qui couvre un manque : les commandes en cours par date d'arrivée jusqu'à le couvrir, puis le
 * délai fournisseur pour le reste. leadTime vaut null tant qu'un des deux morceaux n'a pas de date
 * ou de délai.
 */
export function supply(missing, orders, leadTime, today, listUrl) {
    const used = [];
    let covered = 0;
    for (const order of orders || []) {
        if (covered >= missing - EPSILON) {
            break;
        }
        used.push(order);
        covered += parseFloat(order.remaining) || 0;
    }

    if (used.length === 0) {
        return { incoming: null, rest: null, leadTime: leadTime || null };
    }

    const last = used[used.length - 1];
    const date = last.delivery_at || null;
    const rest = Math.max(0, missing - covered);
    const incoming = {
        quantity: Math.min(missing, covered),
        count: used.length,
        date: date,
        late: date !== null && date < today,
        orders: used,
        url: used.length === 1 ? (used[0].url || null) : (listUrl || null),
    };

    const arrival = date === null ? null : { days: Math.max(0, daysBetween(today, date)), origin: 'Commande ' + last.number };
    if (rest <= EPSILON) {
        return { incoming: incoming, rest: null, leadTime: arrival };
    }

    const slowest = arrival && leadTime ? (leadTime.days > arrival.days ? leadTime : arrival) : null;

    return { incoming: incoming, rest: { quantity: rest, lead_time: leadTime || null }, leadTime: slowest };
}

/** Un article simple : ses données brutes viennent de lines.availability. */
function articleAvailability(line, article, quantity, demand, today) {
    if (!article.is_manage) {
        return {
            stock: { label: 'Non suivi', color: null, tooltip: null },
            status: article.lead_time ? 'delay' : null,
            leadTime: article.lead_time,
            limiting: null,
            incoming: null,
            rest: null,
            components: {},
        };
    }

    const entry = demand ? demand[line.article_id] : null;
    const stock = parseFloat(article.available_stock) || 0;
    const need = needOf(demand, line.article_id, quantity);
    const covers = stock >= need - EPSILON;
    const covering = covers ? null : supply(need - stock, article.incoming, article.lead_time, today, article.incoming_url);

    return {
        stock: {
            label: format(stock) + ' ' + (article.unit || 'U'),
            color: stockColor(stock, covers),
            tooltip: isShared(entry) ? demandLabel(entry, article.unit) : null,
        },
        status: covers ? 'available' : (covering.leadTime ? 'delay' : 'unknown'),
        leadTime: covering ? covering.leadTime : null,
        limiting: null,
        incoming: covering ? covering.incoming : null,
        rest: covering ? covering.rest : null,
        components: {},
        unit: article.unit || 'U',
    };
}

/** Un kit est « en réception » quand les commandes couvrent tous ses composants manquants ; sa date est la plus tardive, absente si l'une manque. */
function kitIncoming(supplies, today) {
    const all = Object.values(supplies);
    if (all.some(item => !item.incoming || item.rest)) {
        return null;
    }

    const dates = all.map(item => item.incoming.date);
    const date = dates.includes(null) ? null : dates.reduce((latest, value) => value > latest ? value : latest);

    return { quantity: null, count: null, date: date, late: date !== null && date < today, orders: [], url: null };
}

/**
 * Un kit : disponible si chaque composant suivi couvre le besoin du devis entier, sinon chaque
 * composant qui manque est couvert par ses commandes puis son délai, et le kit prend le plus
 * tardif ; un composant manquant sans date ni délai rend le délai inconnu.
 */
function kitAvailability(kit, quantity, demand, today) {
    if (kit.assemblable === null || kit.assemblable === undefined) {
        return { stock: { label: 'Non suivi', color: null, tooltip: null }, status: null, leadTime: null, limiting: null, incoming: null, rest: null, components: {} };
    }

    const needs = {};
    const missing = kit.components.filter(component => {
        needs[component.article_id] = needOf(demand, component.article_id, component.quantity * quantity);

        return component.is_manage && (parseFloat(component.available_stock) || 0) < needs[component.article_id] - EPSILON;
    });

    const shared = kit.components.filter(component => component.is_manage && isShared(demand ? demand[component.article_id] : null));
    const stock = {
        label: kit.assemblable + (kit.assemblable > 1 ? ' kits' : ' kit'),
        color: stockColor(kit.assemblable, missing.length === 0),
        tooltip: shared.length > 0
            ? shared.map(component => (component.number || component.name) + ' : ' + format(component.available_stock) + ' en stock\n'
                + demandLabel(demand[component.article_id], component.unit)).join('\n')
            : null,
    };

    if (missing.length === 0) {
        return { stock: stock, status: 'available', leadTime: null, limiting: null, incoming: null, rest: null, components: {} };
    }

    const supplies = {};
    missing.forEach(component => {
        supplies[component.article_id] = supply(needs[component.article_id] - (parseFloat(component.available_stock) || 0), component.incoming, component.lead_time, today, component.incoming_url);
    });

    const unknown = missing.find(component => !supplies[component.article_id].leadTime);
    if (unknown) {
        return { stock: stock, status: 'unknown', leadTime: null, limiting: unknown, incoming: kitIncoming(supplies, today), rest: null, components: supplies };
    }

    const slowest = missing.reduce((current, component) => {
        return !current || supplies[component.article_id].leadTime.days > supplies[current.article_id].leadTime.days ? component : current;
    }, null);
    const leadTime = supplies[slowest.article_id].leadTime;

    return {
        stock: stock,
        status: 'delay',
        leadTime: { days: leadTime.days, origin: 'Composant ' + (slowest.number || slowest.name) + '\n' + leadTime.origin },
        limiting: slowest,
        incoming: kitIncoming(supplies, today),
        rest: null,
        components: supplies,
    };
}

/**
 * Le besoin de chaque article sur tout le devis : ses lignes, plus ce qu'en consomment les
 * kits (quantité par kit × nombre de kits). [article_id => { quantity, lines, kits }]
 */
export function documentDemand(lines) {
    const demand = {};
    const add = (articleId, quantity, key) => {
        const entry = demand[articleId] || { quantity: 0, lines: 0, kits: 0 };
        entry.quantity += quantity;
        entry[key] += 1;
        demand[articleId] = entry;
    };

    lines.forEach(line => {
        if (!isCountable(line)) {
            return;
        }

        const quantity = parseFloat(line.quantity) || 0;

        if (line.kit) {
            line.kit.components.forEach(component => add(component.article_id, component.quantity * quantity, 'kits'));
        } else {
            add(line.article_id, quantity, 'lines');
        }
    });

    return demand;
}

/** La disponibilité d'une ligne ; null pour un titre, une éco-participation, une ligne libre, un forfait ou des données pas encore chargées. */
export function lineAvailability(line, articles, demand, today) {
    if (!isCountable(line)) {
        return null;
    }

    const quantity = parseFloat(line.quantity) || 0;
    const day = today || todayString();

    if (line.kit) {
        return kitAvailability(line.kit, quantity, demand, day);
    }

    const article = (articles || {})[line.article_id];

    return article ? articleAvailability(line, article, quantity, demand, day) : null;
}

/**
 * Le délai de livraison du devis entier : la ligne la plus tardive et son délai, et les lignes
 * dont le délai est inconnu. null sans ligne d'article.
 */
export function deliverySummary(rows) {
    const known = rows.filter(row => row.availability && row.availability.status !== null);
    if (known.length === 0) {
        return null;
    }

    const latest = known
        .filter(row => row.availability.status === 'delay')
        .reduce((current, row) => {
            return !current || row.availability.leadTime.days > current.availability.leadTime.days ? row : current;
        }, null);

    return {
        latest: latest ? reference(latest) : null,
        days: latest ? latest.availability.leadTime.days : null,
        unknown: known.filter(row => row.availability.status === 'unknown').map(row => reference(row)),
    };
}

/** La référence d'une ligne ; pour un kit, le composant qui donne le délai suivi du kit. */
function reference(row) {
    const own = row.line.reference || row.line.designation;
    const limiting = row.availability.limiting;

    return limiting ? (limiting.number || limiting.name) + ' (' + own + ')' : own;
}

/** Une date AAAA-MM-JJ en JJ/MM/AAAA. */
export function formatDate(date) {
    return date ? date.split('-').reverse().join('/') : '';
}

/** Une date courte : JJ/MM dans l'année en cours, JJ/MM/AAAA au-delà. */
function shortDate(date, today) {
    return date.slice(0, 4) === today.slice(0, 4) ? date.slice(5).split('-').reverse().join('/') : formatDate(date);
}

/**
 * Ce que dit la cellule Disponibilité d'une part en réception : [label, tooltip, late, url].
 * Le badge tient en une ligne (« 100 U attendus le 27/10 ») ; les commandes, leurs dates et le retard
 * sont dans l'infobulle, une information par ligne.
 */
export function incomingText(incoming, unit, today) {
    const day = today || todayString();
    const plural = incoming.quantity === null || incoming.quantity > 1;
    const subject = incoming.quantity === null ? 'Attendu' : format(incoming.quantity) + ' ' + (unit || 'U') + (plural ? ' attendus' : ' attendu');
    const label = subject + (incoming.date ? ' le ' + shortDate(incoming.date, day) : ', sans date');

    const lines = incoming.orders.map(order => order.number + ' · ' + order.company + ' · ' + format(order.remaining) + ' ' + (unit || 'U')
        + ' · ' + (order.delivery_at ? formatDate(order.delivery_at) : 'sans date'));
    if (incoming.late) {
        lines.push('En retard');
    }
    lines.push(incoming.date ? 'Livraison souhaitée sur la commande' : 'Date de livraison non renseignée sur la commande');

    return { label: label, tooltip: lines.join('\n'), late: incoming.late, url: incoming.url };
}

/** « 740 U sous 14 j » ou « 740 U, délai inconnu » : le reste suit le délai fournisseur. */
export function restText(rest, unit) {
    return format(rest.quantity) + ' ' + (unit || 'U') + (rest.lead_time ? ' sous ' + rest.lead_time.days + ' j' : ', délai inconnu');
}

/** L'infobulle du reste : la mention du PDF et l'origine du délai. */
export function restTooltip(rest) {
    return 'Suivant délai fournisseur\n' + (rest.lead_time ? rest.lead_time.origin : 'Aucun délai de référence ni fournisseur');
}
